import { useState, useEffect, useCallback } from 'react';
import { paymentService, profileService, subscriptionService, licenseTokenService } from '../services/api';

/**
 * Hook for fetching pricing plans
 */
export function usePlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await paymentService.getPlans();
      if (response.data.success) {
        const rawPlans = response.data.data || response.data.plans || [];
        // Normalize plan fields for frontend compatibility
        const normalizedPlans = rawPlans.map(plan => {
          const isBasic = plan.name === 'Basic';
          const features = (plan.features || []).map(f =>
            f === 'mToken USB DSC support' ? 'DSC USB support' : f
          );
          if (isBasic) {
            // Basic covers 1-4 licences, billed per licence
            features.push('1–4 device licences');
          }
          return {
            ...plan,
            features: isBasic
              ? [...features.filter(f => f !== 'Standard support'), 'Standard support']
              : features,
            min_dongles: isBasic ? 1 : plan.min_dongles,
            max_dongles: isBasic ? 4 : plan.max_dongles,
            price: parseFloat(plan.base_price) || parseFloat(plan.price) || 0,
            dongle_price: parseFloat(plan.additional_dongle_price) || parseFloat(plan.dongle_price) || 5,
          };
        });
        setPlans(normalizedPlans);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return { plans, loading, error, refetch: fetchPlans };
}

/**
 * Hook for fetching current subscription
 */
export function useSubscription() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [subResponse, plansResponse] = await Promise.all([
        subscriptionService.getCurrent(),
        paymentService.getPlans().catch(() => ({ data: { success: false } })),
      ]);
      
      if (subResponse.data.success) {
        // API returns { data: { has_active_subscription, subscription } }
        const data = subResponse.data.data;
        const sub = data?.subscription || null;
        
        // Normalize field names for frontend
        if (sub) {
          sub.start_date = sub.subscription_datetime;
          sub.end_date = sub.expiry_datetime;
          sub.total_days = sub.plan_limit_days;
          
          // Fetch plan details if not already included
          if (!sub.plan && sub.plan_id && plansResponse.data.success) {
            const plans = plansResponse.data.data || plansResponse.data.plans || [];
            const matchedPlan = plans.find(p => p.id === sub.plan_id);
            if (matchedPlan) {
              sub.plan = matchedPlan;
              sub.plan_name = matchedPlan.name;
            }
          }
        }
        setSubscription(sub);
      }
    } catch (err) {
      // 404 means no active subscription
      if (err.response?.status === 404) {
        setSubscription(null);
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to fetch subscription');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  return { subscription, loading, error, refetch: fetchSubscription };
}

/**
 * Hook for free trial status (active / expired / none).
 * Uses the current subscription first, then falls back to subscription
 * history so an expired trial can still be shown with its dates.
 */
export function useTrialStatus() {
  const [trial, setTrial] = useState({ state: 'none' });
  const [loading, setLoading] = useState(true);

  const fetchTrial = useCallback(async () => {
    setLoading(true);
    const toTrial = (sub) => {
      const endDate = sub.expiry_datetime || sub.end_date;
      const startDate = sub.subscription_datetime || sub.start_date;
      const end = endDate ? new Date(endDate) : null;
      const msLeft = end && !isNaN(end.getTime()) ? end - new Date() : 0;
      const status = (sub.status || '').toLowerCase();
      const expired = msLeft <= 0 || status === 'expired' || status === 'inactive';
      return {
        state: expired ? 'expired' : 'active',
        startDate,
        endDate,
        daysRemaining: Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24))),
        totalDays: sub.plan_limit_days || sub.total_days || null,
        licences: sub.number_of_dongles || 1,
      };
    };

    try {
      const current = await subscriptionService.getCurrent().catch(() => null);
      const currentSub = current?.data?.data?.subscription;
      if (currentSub?.is_trial) {
        setTrial(toTrial(currentSub));
        return;
      }

      const history = await profileService.getSubscriptionHistory().catch(() => null);
      const raw = history?.data?.data;
      const list = Array.isArray(raw) ? raw : raw?.subscriptions || history?.data?.subscriptions || [];
      const trialSub = list.find(s => s.is_trial);
      if (trialSub) {
        const t = toTrial(trialSub);
        // A trial in history that isn't the current subscription has ended
        setTrial(currentSub ? { ...t, state: 'expired', upgraded: true } : t);
      } else {
        setTrial({ state: 'none' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrial();
  }, [fetchTrial]);

  return { trial, loading, refetch: fetchTrial };
}

/**
 * Hook for fetching subscription access token
 */
export function useAccessToken() {
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAccessToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await subscriptionService.getAccessToken();
      if (response.data.success) {
        setAccessToken(response.data.data?.access_token || response.data.access_token || null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setAccessToken(null);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch access token');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccessToken();
  }, [fetchAccessToken]);

  return { accessToken, loading, error, refetch: fetchAccessToken };
}

/**
 * Hook for fetching license tokens and usage summary
 */
export function useLicenseTokens() {
  const [tokens, setTokens] = useState([]);
  const [usage, setUsage] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // silent: refresh without showing the loading state (e.g. after saving)
  const fetchTokens = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      const response = await licenseTokenService.list();
      if (response.data.success) {
        setTokens(response.data.data?.tokens || []);
        setUsage(response.data.data?.usage || null);
        setDocuments(response.data.data?.documents || null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch license tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  return { tokens, usage, documents, loading, error, refetch: fetchTokens };
}

/**
 * Hook for fetching transactions
 */
export function useTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await paymentService.getTransactions();
      if (response.data.success) {
        setTransactions(response.data.data || response.data.transactions || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return { transactions, loading, error, refetch: fetchTransactions };
}

/**
 * Hook for fetching orders
 */
export function useOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await paymentService.getOrders();
      if (response.data.success) {
        setOrders(response.data.data || response.data.orders || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { orders, loading, error, refetch: fetchOrders };
}

/**
 * Hook for fetching user profile
 */
export function useProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await profileService.getProfile();
      if (response.data.success) {
        setProfile(response.data.data || response.data.user || response.data.profile || null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (data) => {
    try {
      const response = await profileService.updateProfile(data);
      if (response.data.success) {
        setProfile(prev => ({ ...prev, ...data }));
        return { success: true };
      }
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to update profile');
    }
  };

  return { profile, loading, error, refetch: fetchProfile, updateProfile };
}

/**
 * Hook for fetching subscription history
 */
export function useSubscriptionHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await profileService.getSubscriptionHistory();
      if (response.data.success) {
        setHistory(response.data.data || response.data.subscriptions || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch subscription history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { history, loading, error, refetch: fetchHistory };
}

/**
 * Hook for reminder settings
 */
export function useReminders() {
  const [reminderStatus, setReminderStatus] = useState(null);
  const [expiringSubscriptions, setExpiringSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReminderData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statusRes, expiringRes] = await Promise.all([
        profileService.getReminderStatus().catch(() => ({ data: { success: false } })),
        profileService.getExpiringSubscriptions(7).catch(() => ({ data: { success: false } })),
      ]);

      if (statusRes.data.success) {
        setReminderStatus(statusRes.data.data || statusRes.data);
      }
      
      if (expiringRes.data.success) {
        setExpiringSubscriptions(expiringRes.data.data || expiringRes.data.subscriptions || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch reminder data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminderData();
  }, [fetchReminderData]);

  const toggleReminder = async (enable) => {
    try {
      const response = await profileService.toggleAutoReminder(enable);
      if (response.data.success) {
        setReminderStatus(prev => ({ ...prev, auto_reminder: enable }));
        return { success: true };
      }
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Failed to update reminder settings');
    }
  };

  return { 
    reminderStatus, 
    expiringSubscriptions, 
    loading, 
    error, 
    refetch: fetchReminderData,
    toggleReminder 
  };
}

/**
 * Hook for calculating order amount
 */
export function useOrderCalculation() {
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculate = useCallback(async (planId, numberOfDongles) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await paymentService.calculateAmount({
        plan_id: planId,
        number_of_dongles: numberOfDongles,
      });
      
      if (response.data.success) {
        const result = response.data.data || response.data;
        setCalculation(result);
        return result;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to calculate amount';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setCalculation(null);
    setError(null);
  }, []);

  return { calculation, loading, error, calculate, reset };
}

/**
 * Hook for dashboard stats
 */
export function useDashboardStats() {
  const [stats, setStats] = useState({
    activeSubscriptions: 0,
    totalDongles: 0,
    daysRemaining: 0,
    totalSpent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [subscriptionRes, ordersRes] = await Promise.all([
        paymentService.getCurrentSubscription().catch(() => ({ data: { success: false } })),
        paymentService.getOrders().catch(() => ({ data: { success: false } })),
      ]);

      const subscription = subscriptionRes.data.success 
        ? (subscriptionRes.data.data || subscriptionRes.data.subscription) 
        : null;
      
      const orders = ordersRes.data.success 
        ? (ordersRes.data.data || ordersRes.data.orders || []) 
        : [];

      // Calculate stats
      const activeSubscriptions = subscription ? 1 : 0;
      const totalDongles = subscription?.number_of_dongles || 0;
      
      // Calculate days remaining
      let daysRemaining = 0;
      if (subscription?.end_date) {
        const endDate = new Date(subscription.end_date);
        const today = new Date();
        daysRemaining = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
      }
      
      // Calculate total spent
      const totalSpent = orders
        .filter(order => order.status === 'completed' || order.status === 'paid')
        .reduce((sum, order) => sum + (parseFloat(order.amount) || 0), 0);

      setStats({
        activeSubscriptions,
        totalDongles,
        daysRemaining,
        totalSpent,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}
